import { useTranslations } from 'hooks/useTranslations';
import * as yup from 'yup';
import { ETransactionTypes } from 'constants/TransactionTypes';
import {
  TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD,
  TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_NOT_MANDATORY,
} from 'pages/LegalEntitiesPage/LegalEntityAddFirearmPage/index';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { CHARS_LIMIT } from 'components/organisms/FormCustomField';

export const useAddFirearmFormSchema = (customFields: ICustomFieldByObjectType[]) => {
  const { t } = useTranslations();

  const customFieldsValidationsOne: Record<string, any> = {};
  const customFieldsValidationsMany: Record<string, any> = {};

  customFields.forEach((customField) => {
    const baseValidation = yup.string().nullable();

    if (customField.customFieldType === ECustomFieldType.TEXT && customField.mandatory) {
      customFieldsValidationsOne[customField.name] = baseValidation.when('firearmsQuantity', {
        is: 'one',
        then: (schema) =>
          schema
            .required(t('general.field.validation.required'))
            .max(
              CHARS_LIMIT,
              t('general.field.validation.maxCharacters').replace(
                '{0}',
                CHARS_LIMIT.toString(),
              ),
            ),
        otherwise: (schema) => schema,
      });
      customFieldsValidationsMany[customField.name] = yup
        .string()
        .nullable()
        .required(t('general.field.validation.required'))
        .max(
          CHARS_LIMIT,
          t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
        );
    } else if (customField.customFieldType === ECustomFieldType.TEXT) {
      customFieldsValidationsOne[customField.name] = baseValidation.when('firearmsQuantity', {
        is: 'one',
        then: (schema) =>
          schema.max(
            CHARS_LIMIT,
            t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
          ),
        otherwise: (schema) => schema,
      });
      customFieldsValidationsMany[customField.name] = yup
        .string()
        .nullable()
        .max(
          CHARS_LIMIT,
          t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
        );
    } else if (customField.mandatory) {
      customFieldsValidationsOne[customField.name] = baseValidation.when('firearmsQuantity', {
        is: 'one',
        then: (schema) => schema.required(t('general.field.validation.required')),
        otherwise: (schema) => schema,
      });
      customFieldsValidationsMany[customField.name] = yup
        .string()
        .nullable()
        .required(t('general.field.validation.required'));
    }
  });

  yup.addMethod(yup.array, 'unique', function (message, mapper = (a: any) => a) {
    return this.test('unique', message, (list: any[] | undefined) =>
      list ? list.length === new Set(list.map(mapper)).size : true,
    );
  });

  const schemaLE = yup.object({
    typeUniqueId: yup.string().required(t('general.field.validation.required')),

    attachments: yup.array().of(
      yup.object({
        attachmentName: yup
          .string()
          .test('unique', t('general.field.validation.unique'), (value, params) => {
            const attachments = (params?.from?.[1]?.value?.attachments ?? []) as any[];
            if (!value) return true;
            if (attachments.filter((r) => r.attachmentName === value).length > 1) {
              return params?.createError({
                path: params.path,
                message: t('general.field.validation.unique'),
              });
            }
            return true;
          })
          .required(t('general.field.validation.required'))
          .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
          .matches(/^[a-zA-Z0-9_ -]+$/, t('general.field.validation.attachmentName'))
          .trim(),
      }),
    ),

    transactionDate: yup.string().when('validationOff', {
      is: false,
      then: (schema) =>
        schema
          .required(t('general.field.validation.required'))
          .test(
            'date-validation',
            t('legalEntity.validation.maxDate').replace('{0}', '255'),
            (value) => {
              if (!value) return false;
              const maxValidDate = new Date();
              const passedDate = new Date(value);
              return passedDate.valueOf() <= maxValidDate.valueOf();
            },
          ),
      otherwise: (schema) => schema.nullable(),
    }),

    isExhibitNumberChecked: yup.boolean(),
    validationOff: yup.boolean(),

    serialNumber: yup
      .string()
      .nullable()
      .when(['firearmsQuantity', 'validationOff'], {
        is: (firearmsQuantity: string, validationOff: boolean) =>
          firearmsQuantity === 'one' && !validationOff,
        then: (schema) =>
          schema
            .max(20, t('general.field.validation.maxCharacters').replace('{0}', '20'))
            .when('isExhibitNumberChecked', {
              is: false,
              then: (s) => s.required(t('general.field.validation.required')),
              otherwise: (s) => s,
            }),
        otherwise: (schema) => schema,
      }),

    serialNumberConfirmation: yup
      .string()
      .nullable()
      .when(['firearmsQuantity', 'validationOff'], {
        is: (firearmsQuantity: string, validationOff: boolean) =>
          firearmsQuantity === 'one' && !validationOff,
        then: (schema) =>
          schema.when('isExhibitNumberChecked', {
            is: false,
            then: (s) => s.required(t('general.field.validation.required')),
            otherwise: (s) => s,
          }),
        otherwise: (schema) => schema,
      })
      .test(
        'serialNumber-confirmation-validation',
        t('general.field.validation.serialNumberConfirmationInvalid'),
        (value, obj) =>
          !(
            obj.parent.firearmsQuantity === 'one' &&
            !obj.parent.validationOff &&
            !obj.parent.isExhibitNumberChecked &&
            obj.parent.serialNumber &&
            value &&
            obj.parent.serialNumber !== value
          ),
      ),

    ...customFieldsValidationsOne,

    appliedMarking: yup.string().when('firearmsQuantity', {
      is: 'one',
      then: (schema) =>
        schema.max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
      otherwise: (schema) => schema,
    }),
    importMarking: yup.string().when('firearmsQuantity', {
      is: 'one',
      then: (schema) =>
        schema.max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
      otherwise: (schema) => schema,
    }),

    exhibitNumber: yup
      .string()
      .nullable()
      .when('firearmsQuantity', {
        is: 'one',
        then: (schema) =>
          schema
            .max(15, t('general.field.validation.maxCharacters').replace('{0}', '15'))
            .when('isExhibitNumberChecked', {
              is: true,
              then: (s) => s.required(t('general.field.validation.required')),
              otherwise: (s) => s,
            }),
        otherwise: (schema) => schema,
      }),

    manufacturedAt: yup
      .string()
      .nullable()
      .test(
        'manufacture-date-validation',
        t('legalEntity.validation.maxDate'),
        (value, obj) => {
          const parent: any = obj?.parent || {};
          if (
            !TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(
              parent.typeUniqueId,
            )
          )
            return true;
          if (!value) return true;
          const maxValidDate = new Date();
          const passedDate = new Date(value);
          return passedDate.valueOf() <= maxValidDate.valueOf();
        },
      )
      .when(['firearmsQuantity', 'typeUniqueId'], {
        is: (firearmsQuantity: string, typeUniqueId: ETransactionTypes) =>
          firearmsQuantity === 'one' &&
          TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(typeUniqueId) &&
          !TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_NOT_MANDATORY.includes(
            typeUniqueId,
          ),
        then: (schema) => schema.required(t('general.field.validation.required')),
        otherwise: (schema) => schema,
      }),

    firearmsList: yup
      .array()
      .nullable()
      .when(['firearmsQuantity', 'validationOff'], {
        is: (firearmsQuantity: string, validationOff: boolean) =>
          firearmsQuantity === 'many' && !validationOff,
        then: (schema) =>
          schema.of(
            yup.object({
              isExhibitNumberChecked: yup.boolean(),
              serialNumber: yup
                .string()
                .max(20, t('general.field.validation.maxCharacters').replace('{0}', '20'))
                .when('isExhibitNumberChecked', {
                  is: false,
                  then: (s) => s.required(t('general.field.validation.required')),
                  otherwise: (s) => s,
                })
                .test('uniqueSerialNumber', t('addAncillary.fieldUnique.text'), (val, obj) => {
                  if (!val) {
                    return true;
                  }
                  const firearmsList = (obj as any)?.from?.[1]?.value?.firearmsList;
                  if (
                    firearmsList &&
                    firearmsList.filter((item: any) => item.serialNumber === val).length > 1
                  ) {
                    return false;
                  }
                  return true;
                }),
              serialNumberConfirmation: yup.string().when('isExhibitNumberChecked', {
                is: false,
                then: (s) =>
                  s
                    .required(t('general.field.validation.required'))
                    .test(
                      'serial-number-match',
                      t('general.field.validation.serialNumberConfirmationInvalid'),
                      function (value) {
                        if (!value) return true;
                        return value === this.parent.serialNumber;
                      },
                    ),
                otherwise: (s) => s,
              }),
              ...customFieldsValidationsMany,
              appliedMarking: yup
                .string()
                .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
              importMarking: yup
                .string()
                .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
              exhibitNumber: yup
                .string()
                .max(15, t('general.field.validation.maxCharacters').replace('{0}', '15'))
                .when('isExhibitNumberChecked', {
                  is: true,
                  then: (s) => s.required(t('general.field.validation.required')),
                  otherwise: (s) => s,
                }),
              manufacturedAt: yup
                .string()
                .nullable()
                .test('manufacturedAt', '', (val, obj) => {
                  const parent: any = obj?.parent || {};
                  if (
                    !val &&
                    TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(
                      parent.typeUniqueId,
                    ) &&
                    !TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_NOT_MANDATORY.includes(
                      parent.typeUniqueId,
                    )
                  ) {
                    return new yup.ValidationError(
                      t('addFirearm.fieldRequired.text'),
                      null,
                      obj.path,
                    );
                  }
                  return true;
                })
                .test(
                  'manufacture-date-validation',
                  t('legalEntity.validation.maxDate'),
                  (value) => {
                    if (!value) return true;
                    const maxValidDate = new Date();
                    const passedDate = new Date(value);
                    return passedDate.valueOf() <= maxValidDate.valueOf();
                  },
                ),
            }),
          ),
        otherwise: (schema) => schema,
      }),

    groupName: yup.string().when(['isCreateGroup', 'firearmsQuantity'], {
      is: (isCreateGroup: string, firearmsQuantity: string) =>
        isCreateGroup === 'yes' && firearmsQuantity === 'many',
      then: (schema) =>
        schema
          .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
          .required(t('general.field.validation.required')),
      otherwise: (schema) => schema,
    }),

    countryOfImportUniqueId: yup.string().when('validationOff', {
      is: false,
      then: (schema) =>
        schema.when('typeUniqueId', {
          is: ETransactionTypes.DECLARED_IMPORTED,
          then: (s) => s.required(t('addFirearm.fieldRequired.text')),
          otherwise: (s) => s.nullable(),
        }),
      otherwise: (schema) => schema.nullable(),
    }),

    importer: yup
      .string()
      .max(250, t('general.field.validation.maxCharacters').replace('{0}', '250')),
    address: yup.string().nullable(),
    unassignedLocation: yup.boolean().nullable(),
    latitude: yup.number().nullable(),
    longitude: yup.number().nullable(),
    countryUniqueId: yup.string().nullable(),
    city: yup.string().nullable(),
  });

  return { schemaLE };
};
